import { FormType } from "@wingspanhq/wingspan-api-sdk";
import { useDownloadPdf } from "../../../Invoices/utils";
import { useWSMutation } from "../../helpers";
import { WSServiceError } from "../../../utils/serviceHelper";
import { formsApi } from "@api";
import { useXWingspanPayerId } from "../../../modules/Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import { queriesPayees } from "@query";

/**
 * Downloads a contractor's W-9 PDF (compliance/forms domain).
 *
 * v1 was one call — `GET /payee/:id/download-w9` → the PDF blob. v3 relocates
 * the W-9 to the forms domain, so this fans out:
 *   1. `getPayee` → the contractor's `payeeAccountId` (the W-9's recipient).
 *   2. `listForms` → their latest `W9` form (`type=W9`, `isLatest`), scoped to
 *      that recipient account.
 *   3. `getFormPdf` → the file. The SDK types `getFormPdf` as `void` (the
 *      generator drops the binary body), so the blob is read off the raw
 *      response via `getFormPdfRaw().raw.blob()` — the standard escape hatch for
 *      binary downloads.
 *
 * The payer scope (`xWingspanAccount`) is carried through, mirroring v1's
 * `X-Wingspan-User`. See V3_MIGRATION_TRACKER.md (compliance/forms).
 */
export const useMutationDownloadPayeeW9 = () => {
  const downloadPdf = useDownloadPdf();
  const xWingspanAccount = useXWingspanPayerId();

  return useWSMutation<
    void,
    WSServiceError,
    {
      payeeId: string;
      filename: string;
    }
  >(async ({ payeeId, filename }) => {
    const payee = await queriesPayees.getPayee.fetchQueryData({
      payeeId,
      xWingspanAccount
    });

    // Guard: without the recipient account the form filter would be unscoped
    // and could return another contractor's W-9.
    if (!payee.payeeAccountId) {
      throw new Error("Contractor account not found for W-9 download");
    }

    // The forms endpoints don't take an account-scope override (unlike
    // PayeesApi) — they scope by the session token; only `getPayee` above is
    // payer-scoped.
    const forms = await formsApi.listForms({
      filter: {
        type: { eq: FormType.W9 },
        recipientAccountId: { eq: payee.payeeAccountId },
        isLatest: { eq: true }
      }
    });

    const formId = forms.data?.[0]?.id;
    if (!formId) {
      throw new Error("No W-9 form found for this contractor");
    }

    const response = await formsApi.getFormPdfRaw({ formId });
    const blob = await response.raw.blob();
    const url = window.URL.createObjectURL(blob);
    downloadPdf(url, `${filename}-w9-form.pdf`);
  });
};
